import { createAsyncThunk } from '@reduxjs/toolkit';

import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import { changeCustomerUsersEnabled, removeCustomerUsers } from './customersReducer';

const ADD_USER_TO_COMPANY = 'customers/ADD_USER_TO_COMPANY';
const REMOVE_USER_FROM_COMPANY_CREATE = 'customers/REMOVE_USER_FROM_COMPANY_CREATE';
const REMOVE_USER_FROM_COMPANY = 'customers/REMOVE_USER_FROM_COMPANY';
const ENABLE_USER = 'customers/ENABLE_USER';
const ADMIN_USER_CHECK = 'customers/ADMIN_USER_CHECK';
const ADD_COMPANY = 'customers/ADD_COMPANY';
const GET_ACCESS_PROJECTS = 'customers/GET_ACCESS_PROJECTS';
const LOAD_MORE_ACCESS_PROJECTS = 'customers/LOAD_MORE_ACCESS_PROJECTS';
const GET_ACCESS_ASSISTANTS = 'customers/GET_ACCESS_ASSISTANTS';
const LOAD_MORE_ACCESS_ASSISTANTS = 'customers/LOAD_MORE_ACCESS_ASSISTANTS';
const GET_CUSTOMERS = 'customers/GET_CUSTOMERS';
const DELETE_CUSTOMER = 'customers/DELETE_CUSTOMER';
const ENABLE_CUSTOMER = 'customers/ENABLE_CUSTOMER';
const GET_CUSTOMER_INFO = 'customers/GET_CUSTOMER_INFO';
const ADD_COMPANY_CUSTOMER = 'customers/ADD_COMPANY_CUSTOMER';
const ENABLE_PROJECT_TO_COMPANY_USER = 'customers/ENABLE_PROJECT_TO_COMPANY_USER';
const ENABLE_PROJECT_TO_COMPANY = 'customers/ENABLE_PROJECT_TO_COMPANY';
const REMOVE_PROJECT_FROM_COMPANY = 'customers/REMOVE_PROJECT_FROM_COMPANY';
const ADD_PROJECT_TO_COMPANY = 'customers/ADD_PROJECT_TO_COMPANY';
const SET_ASSISTANTS_TO_COMPANY = 'customers/SET_ASSISTANTS_TO_COMPANY';
const SET_AI_CHATS_TO_COMPANY = 'customers/SET_AI_CHATS_TO_COMPANY';
const SET_AI_MEDIA_TO_COMPANY = 'customers/SET_AI_MEDIA_TO_COMPANY';

export const addUserToCompany = createAsyncThunk(
  ADD_USER_TO_COMPANY,
  async ({ firstName, lastName, username, password }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        callApi.post(`/api/user/management/add-customer`, {
          firstName,
          lastName,
          username,
          password,
        }),
      {
        successStatusCode: 201,
        onError: (error) => {
          if (error.response.status === 409) {
            dispatch(
              enqueueSnackbar({
                message: 'userWithUsernameAlreadyExists',
                options: {
                  variant: 'error',
                  messageParams: {
                    username,
                  },
                },
              }),
            );
            return rejectWithValue(error);
          } else {
            dispatch(somethingWrongNotification());
            return rejectWithValue(error);
          }
        },
      },
    );

    return response.data;
  },
);

export const removeUserFromCompanyCreate = createAsyncThunk(REMOVE_USER_FROM_COMPANY_CREATE, async ({ id }) => {
  await sendRequest(() =>
    callApi.delete(`/api/user/management`, {
      data: { ids: Array.isArray(id) ? id : [id] },
    }),
  );
});

export const removeUserFromCompany = createAsyncThunk(
  REMOVE_USER_FROM_COMPANY,
  async ({ userId, companyId }, { dispatch }) => {
    await sendRequest(() => callApi.delete(`/api/company?companyId=${companyId}&customerId=${userId}`));

    dispatch(removeCustomerUsers(userId));
  },
);

export const enableUser = createAsyncThunk(
  ENABLE_USER,
  async ({ id, enabled, shouldUpdateCustomerInfo = false }, { dispatch, getState }) => {
    const response = await sendRequest(() =>
      callApi.post(`/api/user/management/${id}/${enabled ? 'disable' : 'enable'}`),
    );

    if (shouldUpdateCustomerInfo) {
      const username = getState().securityNew.currentUserInfo.username;
      dispatch(changeCustomerUsersEnabled({ user: response.data, actionedBy: username }));
    }
  },
);

export const adminUserCheck = createAsyncThunk(ADMIN_USER_CHECK, async ({ roleName, id }) => {
  await sendRequest(() => callApi.post(`/api/user/management/${id}/authorities/${roleName}`));
});

export const addNewCompany = createAsyncThunk(
  ADD_COMPANY,
  async (
    {
      companyName,
      companyEmail,
      companyContact,
      companyCity,
      companyAddress,
      companyZip,
      companyUsers,
      assignedProjects,
      assignedAssistants,
      assignedAIChats,
      assignedAIMedia,
    },
    { dispatch },
  ) => {
    await sendRequest(
      () =>
        callApi.post(`/api/company`, {
          address: companyAddress,
          city: companyCity,
          companyEmail,
          companyName,
          companyProjects: assignedProjects.map(({ id }) => id),
          companyAssistants: assignedAssistants.map(({ id }) => id),
          aiChatTypes: assignedAIChats.map(({ id }) => id),
          aiPlaygroundMediaTypes: assignedAIMedia.map(({ id }) => id),
          contact: companyContact,
          zip: companyZip,
          companyUsers: companyUsers.map(({ id, assignedProjects }) => ({
            assignedProjects,
            userId: id,
          })),
        }),
      { successStatusCode: 201 },
    );

    dispatch(
      enqueueSnackbar({
        message: 'companyHasBeenCreatedSuccessfully',
        options: {
          variant: 'success',
          messageParams: {
            companyName,
          },
        },
      }),
    );
  },
);

const loadAccessProjects = async (
  { page, searchQuery, shouldIgnoreCompanyProjects },
  { dispatch, getState, rejectWithValue },
) => {
  const idsMustNot = shouldIgnoreCompanyProjects
    ? getState().customers.customerInfo.companyProjects.map(({ projectId }) => projectId)
    : [];

  const response = await sendRequest(
    () =>
      callApi.post(`/api/project/search?page=${page}&size=${10}&sort=-createdOn`, {
        ...(searchQuery ? { query: searchQuery, idsMustNot } : { idsMustNot }),
      }),
    {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    },
  );

  return { content: response.data.content, total: response.data.totalElements };
};
export const getAccessProjects = createAsyncThunk(GET_ACCESS_PROJECTS, loadAccessProjects);
export const loadMoreProjects = createAsyncThunk(LOAD_MORE_ACCESS_PROJECTS, loadAccessProjects);

const loadAccessAssistants = async ({ page, searchQuery }, { dispatch, rejectWithValue }) => {
  const response = await sendRequest(
    () =>
      callApi.post(`/api/assistant/search?page=${page}&size=${10}&sort=-name`, {
        ...(searchQuery ? { query: searchQuery } : {}),
      }),
    {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
    },
  );

  return { content: response.data.content, total: response.data.totalElements };
};
export const getAccessAssistants = createAsyncThunk(GET_ACCESS_ASSISTANTS, loadAccessAssistants);
export const loadMoreAssistants = createAsyncThunk(LOAD_MORE_ACCESS_ASSISTANTS, loadAccessAssistants);

export const getCustomers = createAsyncThunk(GET_CUSTOMERS, async (_, { getState }) => {
  const { page, sort, size, searchQuery } = getState().customers.customersTableSettings;

  const response = await sendRequest(() =>
    callApi.get(`/api/company?page=${page + 1}&size=${size}&sort=${sort}&query=${searchQuery}`),
  );

  return { content: response.data.content, total: response.data.totalElements };
});

export const deleteCustomer = createAsyncThunk(DELETE_CUSTOMER, async ({ id, customerName }, { dispatch }) => {
  await sendRequest(() => callApi.delete(`/api/company/${id}`));

  dispatch(
    enqueueSnackbar({
      message: 'companyHasBeenDeletedSuccessfully',
      options: {
        variant: 'success',
        messageParams: {
          customerName,
        },
      },
    }),
  );

  dispatch(getCustomers());
});

export const enableCustomer = createAsyncThunk(ENABLE_CUSTOMER, async ({ id, enabled }) => {
  const response = await sendRequest(() => callApi.post(`/api/company/${enabled ? 'deactivate' : 'activate'}/${id} `));

  return response.data;
});

export const getCustomerInfo = createAsyncThunk(GET_CUSTOMER_INFO, async ({ id }) => {
  const response = await sendRequest(() => callApi.get(`/api/company/${id}`));

  return response.data;
});

export const addCustomerToCompany = createAsyncThunk(
  ADD_COMPANY_CUSTOMER,
  async ({ companyId, firstName, lastName, username, password, assignedProjects }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        callApi.post(`/api/company/add-customer/${companyId}`, {
          firstName,
          lastName,
          username,
          password,
          assignedProjects,
        }),
      {
        onError: (error) => {
          if (error.response.status === 409) {
            dispatch(
              enqueueSnackbar({
                message: 'userWithUsernameAlreadyExists',
                options: {
                  variant: 'error',
                  messageParams: {
                    username,
                  },
                },
              }),
            );
            return rejectWithValue(error);
          } else {
            dispatch(somethingWrongNotification());
            return rejectWithValue(error);
          }
        },
        onSuccess: (response) => {
          dispatch(
            enqueueSnackbar({
              message: 'userHasBeenAddedSuccessfully',
              options: {
                variant: 'success',
                messageParams: {
                  username: response.data.username,
                },
              },
            }),
          );
        },
      },
    );

    return response.data;
  },
);

export const enableProjectToCompanyUser = createAsyncThunk(
  ENABLE_PROJECT_TO_COMPANY_USER,
  async ({ projectId, userId, companyId, enabled }, { dispatch, rejectWithValue }) => {
    await sendRequest(() =>
      callApi.post(
        `/api/company/${
          enabled ? 'enable' : 'disable'
        }/project/to/customer?projectId=${projectId}&userId=${userId}&companyId=${companyId}`,
        {
          onError: (e) => {
            dispatch(somethingWrongNotification());
            return rejectWithValue(e);
          },
        },
      ),
    );

    return { projectId, userId, enabled };
  },
);

export const enableProjectToCompany = createAsyncThunk(
  ENABLE_PROJECT_TO_COMPANY,
  async ({ projectId, companyId, enabled }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        callApi.post(
          `/api/company/${enabled ? 'deactivate' : 'activate'}-project?projectId=${projectId}&companyId=${companyId}`,
        ),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const removeProjectFromCompany = createAsyncThunk(
  REMOVE_PROJECT_FROM_COMPANY,
  async ({ projectId, companyId }, { dispatch, rejectWithValue }) => {
    sendRequest(
      () => callApi.delete(`/api/company/remove/project/from/company?projectId=${projectId}&companyId=${companyId}`),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return projectId;
  },
);

export const addProjectToCompany = createAsyncThunk(
  ADD_PROJECT_TO_COMPANY,
  async ({ projectId, companyId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/company/add-project?projectId=${projectId}&companyId=${companyId}`),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    return response.data;
  },
);

export const setAssistantsToCompany = createAsyncThunk(
  SET_ASSISTANTS_TO_COMPANY,
  async ({ assistants, companyId }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        callApi.post(
          `/api/company/assistants?companyId=${companyId}`,
          assistants.map(({ id }) => id),
        ),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
        onSuccess: () => {
          dispatch(
            enqueueSnackbar({
              message: 'companyAssistantsHasBeenUpdatedSuccessfully',
              options: {
                variant: 'success',
              },
            }),
          );
        },
      },
    );

    return response.data;
  },
);

export const setAiChatsToCompany = createAsyncThunk(
  SET_AI_CHATS_TO_COMPANY,
  async ({ aiChats, companyId }, { dispatch, rejectWithValue }) => {
    const aiChatIds = aiChats.map(({ value }) => value);

    await sendRequest(() => callApi.post(`/api/company/ai-chat?companyId=${companyId}`, aiChatIds), {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
      onSuccess: () => {
        dispatch(
          enqueueSnackbar({ message: 'companyAiChatsHasBeenUpdatedSuccessfully', options: { variant: 'success' } }),
        );
      },
    });

    return aiChatIds;
  },
);

export const setAiMediaToCompany = createAsyncThunk(
  SET_AI_MEDIA_TO_COMPANY,
  async ({ aiMedia, companyId }, { dispatch, rejectWithValue }) => {
    const aiChatIds = aiMedia.map(({ value }) => value);

    await sendRequest(() => callApi.post(`/api/company/ai-media?companyId=${companyId}`, aiChatIds), {
      onError: (e) => {
        dispatch(somethingWrongNotification());
        return rejectWithValue(e);
      },
      onSuccess: () => {
        dispatch(
          enqueueSnackbar({ message: 'companyAiMediaHasBeenUpdatedSuccessfully', options: { variant: 'success' } }),
        );
      },
    });

    return aiChatIds;
  },
);
